import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import { AnalysisResultToStateColor, ANALYSIS_RESULT, STATE_COLOR } from '../components/enums';
import NoItemsOverlay from '../components/no_items_overlay';

// Material UI
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
// import SettingsIcon from '@mui/icons-material/Settings';

// Helper to generate a class name from a color
function colorToClassName(color) {
	if (!color) return '';
	return 'row-color-' + color.replace('#', '').toLowerCase();
}

// DataGrid getRowClassName implementation
const getRowClassName = (params) => {
	const color = AnalysisResultToStateColor(params.row.system_analysis);
	return color ? colorToClassName(color) : '';
};

export default function LocationManagement() {

	const [user, setUser] = useState({});

	const [locationsLoading, setLocationsLoading] = useState(true);

	const [locationsInfo, setLocationsInfo] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
	const [sortModel, setSortModel] = useState([]);
	const [filterModel, setFilterModel] = useState({ items: [] });
	const [density, setDensity] = useState('compact');

	const navigate = useNavigate();

	// Get data about the user on the page
	useEffect(() => {
		axiosInstance.get(`/users/`)
			.then(response => {
				const userInfo = response.data;

				// Check if the user is a superuser or admin - no normal user access
				if (!userInfo || userInfo.length === 0 || (!userInfo.is_staff && !userInfo.is_superuser)) {
					navigate('/403/', { replace: true });
				}

				setUser(response.data);
			})
			.catch(error => {
				console.error(error);
				navigate('/404/', { replace: true });
			});
	}, []);

	// Fetch paginated, sorted, filtered locations info
	useEffect(() => {
		GetLocationsInfo();
	}, [paginationModel, sortModel, filterModel]);

	// Location information retrieval with server-side pagination, sorting, and filtering
	function GetLocationsInfo() {
		setLocationsLoading(true);

		// Build query params
		const params = {
			page_size: paginationModel.pageSize,
			page: paginationModel.page + 1, // API is 1-indexed
		};

		// sorting
		if (sortModel.length > 0) {
			params.sort_field = sortModel[0].field;
			params.sort_order = sortModel[0].sort;
		}
	
		// filtering
		if (filterModel.items.length > 0 && filterModel.items[0].value) {
			params.filter_field = filterModel.items[0].field;
			params.filter_value = filterModel.items[0].value;
		}

		axiosInstance.get(`/map/`, { params })
			.then(async (response) => {
				const { locations, count } = response.data;
				setLocationsInfo(locations);
				setRowCount(count);
			})
			.catch(error => {
				console.error(error);
				setLocationsInfo([]);
			})
			.finally(() => {
				setLocationsLoading(false);
			});
	}

	function GetAnalysisIcon(status) {
		let icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
		let tooltip = "Unknown Status";

		if (
			status === ANALYSIS_RESULT.ANALYSIS_TEMP_SENSOR_ABOVE_DIFF_LIMIT ||
			status === ANALYSIS_RESULT.ANALYSIS_OUT_OF_RANGE_CRITICAL ||
			status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL ||
			status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL
		) {
			icon = <ErrorOutlineIcon style={{ color: 'black' }} />;
			tooltip = "Critical Analysis";
		} else if (
			status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS ||
			status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS
		) {
			icon = <WarningAmberOutlinedIcon style={{ color: STATE_COLOR.MAINTENANCE }} />;
			tooltip = "Maintenance Analysis";
		} else if (
			status === ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS
		) {
			icon = <CheckCircleOutlineOutlinedIcon style={{ color: STATE_COLOR.WITHIN_LIMITS }} />;
			tooltip = "Within Limits";
		} else {
			icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
			tooltip = "Unknown Status";
		}

		return <Tooltip title={tooltip} placement="top">{icon}</Tooltip>;
	}

	// Locations List table columns
	const columns = [
		{
			field: 'system_analysis',
			headerName: 'Ana',
			headerAlign: "left",
			headerClassName: 'hidden-header',
			description: 'Overall System Analysis status',
			width: 55,
			flex: 0,
			filterable: false,
			sortable: false,
			renderCell: (params) => GetAnalysisIcon(params.value)
		},
		{
			field: 'location_name',
			headerName: 'Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Location Name',
			minWidth: 250,
			flex: 1,
		},
		{
			field: 'serial_number',
			headerName: 'Serial #',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Serial Number',
			minWidth: 75,
			maxWidth: 100,
			flex: 1,
		},
		{
			field: 'bvs_model',
			headerName: 'Model',
			headerAlign: "center",
			headerClassName: 'wrap-header-text' ,
			description: 'BVS Controller Model',
			minWidth: 75,
			maxWidth: 100,
			flex: 1,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Organization Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'region',
			headerName: 'Region',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Region Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'facility',
			headerName: 'Facility',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Facility Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'building',
			headerName: 'Building',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Building Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'custom',
			headerName: 'Edit',
			headerAlign: "center",
			description: 'Edit the Location\'s User Permissions',
			width: 65,
			disableColumnSorting: true,
			filterable: false,
			sortable: false,
			hideable: false,
			renderCell: (params)=> {
				return (
					<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<IconButtonWithMenu rowData={params}/>
					</div>
				);
			}
		},
	];
	
	// Build the user edit menu
	function IconButtonWithMenu(rowData) {

		const [anchorEl, setAnchorEl] = useState(null);

		// Menu click
		const handleMenuClick = (event) => {
			setAnchorEl(event.currentTarget);
		};

		const handleMenuClose = () => {
			setAnchorEl(null);
		}

		// Menu Items clicks
		const handleEditClick = () => {
			handleMenuClose();
			navigate(`/admin/locations/${rowData.rowData.id}`);
		};
		
		const handleLocationClick = () => {
			handleMenuClose();
			navigate(`/location/${rowData.rowData.id}`);
		};
		
		// const handleSettingsClick = () => {
		// 	handleMenuClose();
		// 	navigate(`/location/${rowData.rowData.id}/settings`);
		// };

		return (
			<div>
				<IconButton
					aria-label="more"
					aria-controls="long-menu"
					aria-haspopup="true"
					onClick={handleMenuClick}
				>
					<MoreVertIcon />
				</IconButton>
				<Menu
					id="long-menu"
					anchorEl={anchorEl}
					keepMounted
					open={Boolean(anchorEl)}
					onClose={handleMenuClose}
				>
					<MenuItem
						onClick={handleEditClick}
					>
						<ListItemIcon>
							<ManageAccountsIcon fontSize="small" />
						</ListItemIcon>
						Edit Users
					</MenuItem>
					<MenuItem
						onClick={handleLocationClick}
					>
						<ListItemIcon>
							<EditLocationIcon fontSize="small" />
						</ListItemIcon>
						Go to Location
					</MenuItem>
					{/* <MenuItem onClick={handleSettingsClick}>
						<ListItemIcon>
							<SettingsIcon fontSize="small" />
						</ListItemIcon>
						Go to Settings
					</MenuItem> */}
				</Menu>
			</div>
		);
	}

	return (
		<Paper
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				Location Management
			</Typography>
			<style>{`
				.row-color-748592 { background-color: #74859299 !important; }
				.row-color-4b9c2e { background-color: #4b9c2e99 !important; }
				.row-color-f4b235 { background-color: #f4b23599 !important; }
				.row-color-f22635 { background-color: #f2263599 !important; }
			`}</style>
			<DataGrid
				getRowId={(row) => row.location_id}
				rows={locationsInfo}
				columns={columns}
				loading={locationsLoading}
				autoPageSize={false}
				rowCount={rowCount}
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				sortingMode="server"
				sortModel={sortModel}
				onSortModelChange={setSortModel}
				filterMode="server"
				filterModel={filterModel}
				onFilterModelChange={setFilterModel}
				density={density}
				onDensityChange={setDensity}
				getRowClassName={getRowClassName}
				slots={{
					toolbar: (() => (
						<GridToolbarContainer>
							<GridToolbarColumnsButton />
							<GridToolbarFilterButton />
							<GridToolbarDensitySelector />
						</GridToolbarContainer>
					)),
					noRowsOverlay: NoItemsOverlay,
				}}
				slotProps={{
					noRowsOverlay: { noItemsText: 'No Locations' },
				}}
				sx={{
					padding: 2,
				}}
			/>
		</Paper>
	);
}